import React, { useState } from "react";
import InvoiceHeader from "./InvoiceHeader";
import InvoiceRow from "./InvoiceRow";
import MergedInvoiceTable from "./MergedInvoiceTable";
import ActionButtons from "./ActionButtons";
import ReportTable from "./ReportTable";
import ErrorMessage from "../error/Error";

const InvoiceTable = ({ setData,data }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [mergedInvoices, setMergedInvoices] = useState([]);
  const [mergedKeys, setMergedKeys] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(""); // State for error message

  const resetData = () => {
    setSelectedRows(null)
    setMergedInvoices(null)
    setMergedKeys(null)
    setReportData(null)
    setErrorMessage(null)
    setData(null)
  }

  const uniqueInvoices = Array.from(
    new Map(data.map((item) => [item?.VBELN, item])).values()
  );

  const handleSelectAll = (isChecked) => {
    setSelectedRows(isChecked ? uniqueInvoices.map((_, i) => i) : []);
  };

  const handleRowSelect = (index, isChecked) => {
    setSelectedRows((prev) =>
      isChecked ? [...prev, index] : prev.filter((i) => i !== index)
    );
  };

  const roundToTwo = (num) => parseFloat(num.toFixed(2));

  const mergeInvoices = () => {
    const selectedInvoiceNumbers = selectedRows.map(
      (index) => uniqueInvoices[index]?.VBELN
    );

    const selectedInvoices = data.filter((item) =>
      selectedInvoiceNumbers?.includes(item?.VBELN)
    );

    const uniqueCustomerNames = [
      ...new Set(selectedInvoices.map((item) => item.NAME1)),
    ];

    if (uniqueCustomerNames?.length > 1) {
      setErrorMessage(
        "Invoices for different customers cannot be merged. Please verify the invoice numbers and try again."
      );
      return;
    } else {
      setErrorMessage(""); // Clear error if valid selection
    }

    const uniqueSelectedInvoices = Array.from(
      new Map(selectedInvoices.map((item) => [item?.VBELN, item])).values()
    );

    const grouped = selectedInvoices.reduce((acc, product) => {
      const parseNumber = (value) =>
        parseFloat(value?.toString().replace(/,/g, "")) || 0;

      const key = product.MATNR;

      if (!acc[key]) {
        acc[key] = {
          ...product,
          count: 1,
          FKIMG: roundToTwo(parseNumber(product.FKIMG)),
          ZTAMT: roundToTwo(parseNumber(product.ZTAMT)),
          ZDISC: roundToTwo(parseNumber(product.ZDISC)),
          ZPDISC: roundToTwo(parseNumber(product.ZPDISC)),
          ZPVAT: roundToTwo(parseNumber(product.ZPVAT)),
          ZVAT: roundToTwo(parseNumber(product.ZVAT)),
          totalZRATE: parseNumber(product.ZRATE),
        };
      } else {
        acc[key].FKIMG = roundToTwo(acc[key].FKIMG + parseNumber(product.FKIMG));
        acc[key].ZTAMT = roundToTwo(acc[key].ZTAMT + parseNumber(product.ZTAMT));
        acc[key].ZDISC = roundToTwo(acc[key].ZDISC + parseNumber(product.ZDISC));
        acc[key].ZPDISC = roundToTwo(acc[key].ZPDISC + parseNumber(product.ZPDISC));
        acc[key].ZVAT = roundToTwo(acc[key].ZVAT + parseNumber(product.ZVAT));

        acc[key].totalZRATE += parseNumber(product.ZRATE);
        acc[key].count += 1;
      }
      return acc;
    }, {});

    const merged = Object.values(grouped).map((item) => ({
      ...item,
      ZRATE: roundToTwo(item.totalZRATE / item.count),
    }));

    const multipleMergedKeys = merged
      .filter((item) => item.count > 1)
      .map((item) => item.MATNR);

    setMergedInvoices(merged);
    setMergedKeys(multipleMergedKeys);
    setReportData(uniqueSelectedInvoices);
  };

  return (
    <div className="p-6 bg-white shadow-md rounded-lg">
      <h2 className="text-2xl font-semibold mb-6 text-center">Invoice Data</h2>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300 rounded-md">
          <InvoiceHeader
            isChecked={selectedRows?.length === uniqueInvoices?.length}
            onSelectAll={handleSelectAll}
          />
          <tbody>
            {uniqueInvoices.map((item, index) => (
              <InvoiceRow
                key={index}
                item={item}
                isChecked={selectedRows?.includes(index)}
                onRowSelect={(isChecked) => handleRowSelect(index, isChecked)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col items-center justify-center mt-4">
      <ErrorMessage error={errorMessage} show={errorMessage} />

        <ActionButtons onMerge={mergeInvoices} />
        {reportData?.length > 0 && (
          <ReportTable data={reportData} mergedKeys={mergedKeys} />
        )}
      </div>
      {mergedInvoices?.length > 0 && (
        <MergedInvoiceTable
        resetData={resetData}
        invoiceNum = {uniqueInvoices}
          data={mergedInvoices}
          setMergedInvoices={setMergedInvoices}
          mergedKeys={mergedKeys}
        />
      )}
    </div>
  );
};

export default InvoiceTable;
